/**
 * Web3 Provider Configuration
 *
 * Sets up CDP Embedded Wallet + Wagmi for wallet integration.
 * Embedded Wallets use JWT authentication from our backend (no passkeys).
 * Includes Paymaster configuration for gas sponsorship.
 * Wrapped in WalletErrorBoundary for graceful error handling.
 */

import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { WagmiProvider, createConfig, http } from 'wagmi';
import { OnchainKitProvider } from '@coinbase/onchainkit';
import { createCDPEmbeddedWalletConnector } from '@coinbase/cdp-wagmi';
import { CHAIN } from '../config/contracts';
import { WALLET_ENABLED } from '../config/features';
import { WalletErrorBoundary } from '../components/wallet/WalletErrorBoundary';
import { createCdpConfig, initializeCdpSdk, isCdpInitialized } from '../services/cdp-session';
import { useAuthStore } from '../stores/auth.store';

// Paymaster URL for gas sponsorship (required for gas-free transactions)
const PAYMASTER_URL = import.meta.env.VITE_PAYMASTER_URL;

// Create a query client for React Query (required by wagmi)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Don't refetch on window focus for wallet data
      refetchOnWindowFocus: false,
      // Retry failed queries 3 times
      retry: 3,
    },
  },
});

interface Web3ProviderProps {
  children: ReactNode;
}

/**
 * Web3Provider wraps the app with blockchain functionality:
 * - WagmiProvider: Wallet connection and transaction hooks
 * - QueryClientProvider: Caching for blockchain queries
 * - OnchainKitProvider: Coinbase components (FundCard, etc.)
 * - CDP Embedded Wallet: JWT-based wallet authentication
 *
 * Note: This provider should wrap components that need wallet access,
 * but can be placed inside your existing Providers for proper context.
 */
export function Web3Provider({ children }: Web3ProviderProps) {
  // Skip all CDP/wallet initialization when wallet feature is disabled
  if (!WALLET_ENABLED) {
    return <>{children}</>;
  }

  return <Web3ProviderInner>{children}</Web3ProviderInner>;
}

function Web3ProviderInner({ children }: Web3ProviderProps) {
  const apiKey = import.meta.env.VITE_ONCHAINKIT_API_KEY;
  const projectId = import.meta.env.VITE_COINBASE_PROJECT_ID;
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [, setSdkReady] = useState(isCdpInitialized());

  // Initialize CDP SDK when user is authenticated
  useEffect(() => {
    if (isAuthenticated && projectId && !isCdpInitialized()) {
      initializeCdpSdk(projectId)
        .then(() => setSdkReady(true))
        .catch((error) => {
          console.error('Failed to initialize CDP SDK:', error);
        });
    }
  }, [isAuthenticated, projectId]);

  // Create wagmi config with CDP Embedded Wallet connector
  const wagmiConfig = useMemo(() => {
    if (!projectId) return null;

    const cdpConfig = createCdpConfig(projectId);

    const connector = createCDPEmbeddedWalletConnector({
      cdpConfig,
      providerConfig: {
        chains: [CHAIN],
        transports: {
          [CHAIN.id]: http(),
        },
      },
    });

    return createConfig({
      chains: [CHAIN],
      connectors: [connector],
      transports: {
        [CHAIN.id]: http(),
      } as Record<typeof CHAIN.id, ReturnType<typeof http>>,
    });
  }, [projectId]);

  // Validate required environment variables
  if (!apiKey) {
    console.warn('VITE_ONCHAINKIT_API_KEY not set. Embedded Wallet features disabled.');
    return <>{children}</>;
  }

  if (!projectId) {
    console.warn('VITE_COINBASE_PROJECT_ID not set. Embedded Wallet features disabled.');
    return <>{children}</>;
  }

  if (!PAYMASTER_URL) {
    console.error(
      'VITE_PAYMASTER_URL is required for gas sponsorship. Get your Paymaster URL from https://portal.cdp.coinbase.com/ → Paymaster settings.',
    );
    return <>{children}</>;
  }

  if (!wagmiConfig) {
    return <>{children}</>;
  }

  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <OnchainKitProvider
          apiKey={apiKey}
          projectId={projectId}
          chain={CHAIN}
          config={{
            appearance: {
              // Match your app's theme
              mode: 'auto', // 'light' | 'dark' | 'auto'
              theme: 'default',
            },
            // Paymaster configuration for gas sponsorship
            // Transactions use isSponsored={true} to enable gas-free txs
            paymaster: PAYMASTER_URL,
          }}
        >
          <WalletErrorBoundary>{children}</WalletErrorBoundary>
        </OnchainKitProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
}

export default Web3Provider;
